import React, { useState, useRef } from 'react';
import { Box, Typography, InputBase, IconButton, Skeleton, Chip } from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon, Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../lib/axios';
import { useDebounce } from '../hooks/useDebounce';
import { getBranchAvailability } from '../utils/stockAvailability';

// ─── Types ───────────────────────────────────────────────────────────────────

interface BranchStock {
  name: string;
  quantity: number;
}

interface AdminProductItem {
  id: string;
  name: string;
  chainName: string;
  branches: BranchStock[];
  totalQuantity: number;
  isControlled: boolean;
  price: string;
  isRecommended: boolean;
  recommendedAt: string | null;
}

// ─── Colors ──────────────────────────────────────────────────────────────────

const NAVY = '#0D1B35';

// ─── Components ──────────────────────────────────────────────────────────────

const SkeletonCard: React.FC = () => (
  <Box
    sx={{
      background: 'white',
      borderRadius: '14px',
      padding: '14px 16px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      borderLeft: '4px solid #E1E8ED',
    }}
  >
    <Skeleton variant="text" width="60%" height={18} />
    <Skeleton variant="text" width="40%" height={14} sx={{ mt: 0.5 }} />
    <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
      <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: '6px' }} />
      <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: '6px' }} />
    </Box>
  </Box>
);

const AdminProductCard: React.FC<{
  product: AdminProductItem;
  onToggleRecommend: (id: string) => void;
}> = ({ product, onToggleRecommend }) => (
  <Box
    sx={{
      background: 'white',
      borderRadius: '14px',
      padding: '14px 16px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      borderLeft: `4px solid ${product.isControlled ? '#E53935' : '#E1E8ED'}`,
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#14171A', lineHeight: 1.3 }}>
          {product.name}
        </Typography>
        <Typography sx={{ color: '#A0AEC0', fontSize: 10, mt: 0.25 }}>
          {product.chainName}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: NAVY }}>
          ${product.price}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onToggleRecommend(product.id); }}
          sx={{ p: 0.25 }}
        >
          {product.isRecommended ? (
            <StarIcon sx={{ fontSize: 20, color: '#F59E0B' }} />
          ) : (
            <StarBorderIcon sx={{ fontSize: 20, color: '#D1D5DB' }} />
          )}
        </IconButton>
      </Box>
    </Box>

    {/* Branch availability chips */}
    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
      {product.branches.map((branch) => {
        const { label, bg, color } = getBranchAvailability(branch.name, branch.quantity);

        return (
          <Chip
            key={branch.name}
            label={label}
            size="small"
            sx={{
              height: 20,
              background: bg,
              color,
              borderRadius: '6px',
              fontSize: 10,
              fontWeight: 600,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        );
      })}
    </Box>
  </Box>
);

// ─── Main Component ──────────────────────────────────────────────────────────

export const MedicationCatalog: React.FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedTerm = useDebounce(inputValue.trim(), 300);
  const searchTerm = debouncedTerm.length >= 2 ? debouncedTerm : '';

  const { data, isLoading, isError } = useQuery<{
    results: AdminProductItem[];
    total: number;
  }>({
    queryKey: ['admin-pharmacy-search', searchTerm],
    queryFn: async () => {
      const { data } = await apiClient.get('/pharmacy-products/admin/search', {
        params: { q: searchTerm },
      });
      return data;
    },
    enabled: searchTerm.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const queryClient = useQueryClient();

  const recommendMutation = useMutation({
    mutationFn: async (productId: string) => {
      await apiClient.patch(`/pharmacy-products/${productId}/recommend`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pharmacy-search'] });
    },
  });

  const products = data?.results;

  const handleClear = () => {
    setInputValue('');
    inputRef.current?.focus();
  };

  const showEmpty = !searchTerm;
  const showSkeleton = isLoading && searchTerm.length > 0;
  const showError = searchTerm.length > 0 && isError;
  const showResults = searchTerm.length > 0 && !isLoading && !isError && !!products && products.length > 0;
  const showNoResults = searchTerm.length > 0 && !isLoading && !isError && !!products && products.length === 0;

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: '#F0F5FF',
        mx: -2,
        mt: -2,
        px: 2,
        pt: 2,
      }}
    >
      {/* Search bar */}
      <Box
        sx={{
          background: 'white',
          border: focused ? '2px solid #0066CC' : '2px solid #E1E8ED',
          borderRadius: '50px',
          px: 2,
          py: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: focused ? '0 4px 20px rgba(0,102,204,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'border-color 150ms, box-shadow 150ms',
          mb: 1,
        }}
      >
        <SearchIcon sx={{ color: focused ? '#0066CC' : '#A0AEC0', fontSize: 20, flexShrink: 0 }} />
        <InputBase
          inputRef={inputRef}
          fullWidth
          placeholder="Buscar producto en catálogo..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          sx={{ fontSize: 14, py: 1.25, color: '#14171A' }}
          inputProps={{ 'aria-label': 'buscar producto' }}
        />
        {inputValue && (
          <IconButton size="small" onClick={handleClear} sx={{ p: 0.5, color: '#A0AEC0' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Result count */}
      {showResults && (
        <Typography sx={{ fontSize: 11, color: '#A0AEC0', mb: 1, ml: 0.5 }}>
          {products!.length} resultado{products!.length !== 1 ? 's' : ''} encontrado{products!.length !== 1 ? 's' : ''}
        </Typography>
      )}

      {/* Empty state */}
      {showEmpty && (
        <Box
          sx={{
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 0.18 },
              '50%': { transform: 'scale(1.08)', opacity: 0.28 },
            },
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-8px)' },
            },
          }}
        >
          <Box sx={{ textAlign: 'center', pt: 3, mb: 2 }}>
            <Typography variant="h3" sx={{ color: '#14171A', mb: 1 }}>
              Catálogo de Productos
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#A0AEC0' }}>
              Busca por nombre de producto para ver disponibilidad y precios
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '40vh',
            }}
          >
            <Box
              sx={{
                animation: 'pulse 3s ease-in-out infinite, float 4s ease-in-out infinite',
              }}
            >
              <img
                src="/logo-icon.svg"
                alt=""
                style={{ width: 120, height: 120, opacity: 0.6, filter: 'grayscale(30%)' }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Skeleton loading */}
      {showSkeleton && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </Box>
      )}

      {/* Search error */}
      {showError && (
        <Box sx={{ textAlign: 'center', pt: 6 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#14171A' }}>
            Error al buscar
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#A0AEC0', mt: 0.5 }}>
            Verifica tu conexión e intenta de nuevo
          </Typography>
        </Box>
      )}

      {/* No results */}
      {showNoResults && (
        <Box sx={{ textAlign: 'center', pt: 6 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#14171A' }}>
            No encontramos &quot;{searchTerm}&quot;
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#A0AEC0', mt: 0.5 }}>
            Intenta con otro nombre o término
          </Typography>
        </Box>
      )}

      {/* Results */}
      {showResults && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pb: 4 }}>
          {products!.map((product) => (
            <AdminProductCard
              key={product.id}
              product={product}
              onToggleRecommend={(id) => recommendMutation.mutate(id)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
